import { AppForm } from '../../../../../../../common/forms/app.form';
import { FormControl, Validators } from '@angular/forms';
import { CustomValidator, fromToDate } from '../../../../../../../common/forms/custom.validators';
import { ParcelAppEntityParcelWrite, RoadOccupationStageAppEntityApplicationRead } from '../../../../../../../openapi';
import { calcDaysBetweenDates, toNumber } from '../../../../../../../services/helpers';
import {
  RoadControlsForm,
  RoadControlsFormValue
} from '../../../../common/components/road-controls-set/road-controls.form';
import { environment } from '../../../../../../../../environments/environment';

export function toAdPlacementStageFormValue(v: RoadOccupationStageAppEntityApplicationRead): AdPlacementStageFormValue {
  return {
    startDate: v?.startDate || null,
    endDate: v?.endDate || null,

    geometry: v?.geometry || '',

    roadNumber: v?.roadNumber || '',
    roadName: v?.roadName || '',
    street: v?.street || '',

    parcels: v?.parcels || [],

    dayQuantity: v?.dayQuantity || null,
    adQuantity: v?.adQuantity + '',
    adKind: v?.adKind || '',
    adType: v?.adType || '',
    adMountingStructure: v?.adMountingStructure || '',
    adWidth: (v?.adWidth || 0).toFixed(2).replace('.', ','),
    adLength: (v?.adLength || 0).toFixed(2).replace('.', ','),
    adArea: (v?.adArea || 0).toFixed(2).replace('.', ','),
    adLightingType: v?.adLightingType || ''
  };
}

export interface AdPlacementStageFormValue extends RoadControlsFormValue {
  startDate: Date | null;
  endDate: Date | null;
  dayQuantity: number | null;
  geometry: string | null;

  parcels: ParcelAppEntityParcelWrite[];

  adKind: string;
  adType: string;
  adMountingStructure: string;

  adLength: string;
  adWidth: string;
  adArea: string;
  adQuantity: string | null;

  adLightingType: string;
}

export class AdPlacementStageForm extends RoadControlsForm {
  constructor() {
    super({
      startDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat(), CustomValidator.minDate(new Date())] }),
      endDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat()] }),
      dayQuantity: new FormControl(null),
      geometry: new FormControl(null, environment.production ? Validators.required : []),

      parcels: new FormControl([], {
        nonNullable: true,
        validators: [
          Validators.minLength(1),
          Validators.required
        ]
      }),

      adKind: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      adType: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      adMountingStructure: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      adLength: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      adWidth: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      adArea: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      adQuantity: new FormControl(null, [Validators.required, Validators.maxLength(255)]),

      adLightingType: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      })
    }, {
      validators: [
        fromToDate('startDate', 'endDate')
      ]
    });
  }

  public get startDate(): FormControl {
    return this.get('startDate') as FormControl;
  }

  public get endDate(): FormControl {
    return this.get('endDate') as FormControl;
  }

  public get dayQuantity(): FormControl {
    return this.get('dayQuantity') as FormControl;
  }

  public startDateChange(v: Date): void {
    if (v && this.endDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(v), new Date(this.endDate.value))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public endDateChange(v: Date): void {
    if (v && this.startDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(this.startDate.value), new Date(v))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public get adType(): FormControl {
    return this.get('adType') as FormControl;
  }

  public get adWidth(): FormControl {
    return this.get('adWidth') as FormControl;
  }

  public get adLength(): FormControl {
    return this.get('adLength') as FormControl;
  }

  public get adQuantity(): FormControl {
    return this.get('adQuantity') as FormControl;
  }

  public get adArea(): FormControl {
    return this.get('adArea') as FormControl;
  }

  public calcAdArea(): void {
    if (!this.adType.value) {
      this.adArea.setValue('');
      return;
    }
    const multiple: boolean = this.adType.value === 'Dwustronna';
    const adWidth: number = toNumber(this.adWidth.value);
    const adLength: number = toNumber(this.adLength.value);
    const adQuantity: number = toNumber(this.adQuantity.value);
    if (multiple) {
      this.adArea.setValue(`${adWidth * adLength * adQuantity * 2}`);
    } else {
      this.adArea.setValue(`${adWidth * adLength * adQuantity}`);
    }
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  public get values(): AdPlacementStageFormValue {
    return {
      ...this.value,
      roadName: this.roadName.value,
      roadNumber: this.roadNumber.value,
      street: this.street.value
    };
  }
}
